/**
 * Maps `photovogue-api` GraphQL entity shapes onto the module's domain types.
 * Keeps all field-name/shape differences (snake_case, `privateImage`, split names,
 * `isGold`) isolated here so the rest of the app is unaware of the backend shape.
 */

import type {
  Photo,
  Photographer,
  PhotographerState,
  PhotoState,
  PhotoType,
} from './types';

export interface ApiRef {
  id: number | string;
  name: string;
}

export interface ApiPhotoNode {
  id: number | string;
  title: string | null;
  description: string | null;
  created_at: string | null;
  state_updated_at: string | null;
  category: ApiRef | null;
  state: ApiRef | null;
  photographer: {
    id: number | string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  tags: { id: number | string; text: string }[] | null;
  themes: { id: number | string; text: string }[] | null;
  types: ApiRef[] | null;
  imageUrls: {
    thumbnail: string | null;
    small: string | null;
    smallFit: string | null;
    medium: string | null;
    large: string | null;
    gallery: string | null;
    privateImage: string | null;
  } | null;
}

export interface ApiPhotographerNode {
  created_at?: string | null;
  id: number | string;
  first_name: string | null;
  last_name: string | null;
  avatar: string | null;
  city: string | null;
  country: ApiRef | null;
  state: ApiRef | null;
  isGold: boolean | null;
  warning_count: number | null;
  ban_count: number | null;
  total_photos: number | null;
  total_best: number | null;
  total_daily: number | null;
}

export interface ApiAdminDailyPhoto {
  id: number | string;
  title: string | null;
  description: string | null;
  date: string | null;
  daily_date: string;
  category: ApiRef | null;
  state: ApiRef | null;
  photographer: {
    id: number | string;
    name: string;
  } | null;
  types: ApiRef[] | null;
  thumbnail_image: string | null;
  small_image: string | null;
  small_fit_image: string | null;
  medium_image: string | null;
  large_image: string | null;
  gallery_image: string | null;
  private_image: string | null;
}

const PHOTO_STATES: PhotoState[] = [
  'uploaded',
  'ready',
  'approved',
  'rejected',
  'banned',
  'to_fix',
];
const PHOTO_TYPES: PhotoType[] = ['best', 'daily'];
const PHOTOGRAPHER_STATES: PhotographerState[] = [
  'invalid',
  'valid',
  'banned-temporarily',
  'banned-permanently',
  'to_fix',
];

const toPhotoState = (name?: string | null): PhotoState =>
  PHOTO_STATES.includes(name as PhotoState) ? (name as PhotoState) : 'uploaded';

const toPhotographerState = (name?: string | null): PhotographerState =>
  PHOTOGRAPHER_STATES.includes(name as PhotographerState)
    ? (name as PhotographerState)
    : 'invalid';

const toPhotoTypes = (types?: ApiRef[] | null): PhotoType[] =>
  (types ?? [])
    .map((type) => type.name)
    .filter((name): name is PhotoType =>
      PHOTO_TYPES.includes(name as PhotoType),
    );

const fullName = (first?: string | null, last?: string | null): string =>
  `${first ?? ''} ${last ?? ''}`.trim();

export const mapPhoto = (node: ApiPhotoNode): Photo => ({
  id: String(node.id),
  title: node.title ?? '',
  description: node.description ?? '',
  state: toPhotoState(node.state?.name),
  category: node.category?.name ?? '',
  tags: (node.tags ?? []).map((tag) => tag.text),
  themes: (node.themes ?? []).map((theme) => theme.text),
  types: toPhotoTypes(node.types),
  crop: null,
  photographer: {
    id: String(node.photographer?.id ?? ''),
    name: fullName(node.photographer?.first_name, node.photographer?.last_name),
  },
  imageUrls: {
    thumbnail: node.imageUrls?.thumbnail ?? '',
    small: node.imageUrls?.small ?? '',
    smallFit: node.imageUrls?.smallFit ?? '',
    medium: node.imageUrls?.medium ?? '',
    large: node.imageUrls?.large ?? '',
    gallery: node.imageUrls?.gallery ?? '',
    private: node.imageUrls?.privateImage ?? '',
  },
  createdAt: node.created_at ?? '',
  stateUpdatedAt: node.state_updated_at ?? null,
});

export const mapAdminDailyPhoto = (node: ApiAdminDailyPhoto): Photo => ({
  id: String(node.id),
  title: node.title ?? '',
  description: node.description ?? '',
  state: toPhotoState(node.state?.name),
  category: node.category?.name ?? '',
  tags: [],
  themes: [],
  types: toPhotoTypes(node.types),
  crop: null,
  photographer: {
    id: String(node.photographer?.id ?? ''),
    name: node.photographer?.name ?? '',
  },
  imageUrls: {
    thumbnail: node.thumbnail_image ?? '',
    small: node.small_image ?? '',
    smallFit: node.small_fit_image ?? '',
    medium: node.medium_image ?? '',
    large: node.large_image ?? '',
    gallery: node.gallery_image ?? '',
    private: node.private_image ?? '',
  },
  createdAt: node.date ?? '',
  stateUpdatedAt: null,
});

export const mapPhotographer = (node: ApiPhotographerNode): Photographer => ({
  id: String(node.id),
  name: fullName(node.first_name, node.last_name),
  createdAt: node.created_at ?? null,
  avatar: node.avatar ?? '',
  city: node.city ?? '',
  country: node.country?.name ?? '',
  state: toPhotographerState(node.state?.name),
  tier: node.isGold ? 'gold' : 'basic',
  warningCount: node.warning_count ?? 0,
  banCount: node.ban_count ?? null,
  totalPhotos: node.total_photos ?? 0,
  totalBest: node.total_best ?? 0,
  totalDaily: node.total_daily ?? 0,
});
